import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.config({ limitCallbacks: true });

const registry = new Map();
const textReveals = new Map();
const motionReduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

// Cinema profile = desktop width AND a real pointer. Phones and touch tablets
// (hover: none / pointer: coarse) use static content without scroll animations.
// GSAP matchMedia creates/reverts the contexts automatically across breakpoints.
const CINEMA_QUERY = '(min-width: 700px) and (hover: hover) and (pointer: fine)';
const cinemaQuery = window.matchMedia(CINEMA_QUERY);

// Single source of truth for the cinema body class (drives the heavy CSS:
// big stacking gaps, will-change). Set up lazily in initHomepageScroll.
let cinemaMedia = null;

function setupCinemaBodyClass() {
  if (cinemaMedia) return;

  cinemaMedia = gsap.matchMedia();
  cinemaMedia.add(CINEMA_QUERY, () => {
    document.body.classList.add('js-homepage-scroll-cinema');
    return () => document.body.classList.remove('js-homepage-scroll-cinema');
  });
}

function teardownCinemaBodyClass() {
  if (!cinemaMedia) return;

  cinemaMedia.kill();
  cinemaMedia = null;
  document.body.classList.remove('js-homepage-scroll-cinema');
}

function findSectionElement(root, wrapperSelector, innerSelector = wrapperSelector) {
  if (root?.matches?.(innerSelector)) return root;

  const wrapper = root?.matches?.(wrapperSelector)
    ? root
    : root?.querySelector?.(wrapperSelector) ?? root?.closest?.(wrapperSelector);

  if (!wrapper) return null;
  if (wrapper.matches(innerSelector)) return wrapper;

  return wrapper.querySelector(innerSelector);
}

function isEnabled() {
  const { pageType, enableScrollAnimations } = window.themeVariables?.settings ?? {};

  if (pageType !== 'index') return false;
  if (enableScrollAnimations === false) return false;
  if (motionReduceQuery.matches || !cinemaQuery.matches) return false;

  return true;
}

function getSectionId(section) {
  const wrapper = section?.closest?.('.shopify-section') ?? section;
  return wrapper?.id || `homepage-scroll-${registry.size + 1}`;
}

function remember(section, animation) {
  if (!section || !animation) return animation;

  const sectionId = getSectionId(section);
  const entry = registry.get(sectionId) ?? [];

  entry.push(animation);
  registry.set(sectionId, entry);

  return animation;
}

function killAnimation(animation) {
  if (!animation) return;

  if (animation.scrollTrigger) {
    animation.scrollTrigger.kill();
  }

  if (typeof animation.kill === 'function') {
    animation.kill();
  }
}

function killSection(section) {
  const sectionId = getSectionId(section);
  const animations = registry.get(sectionId) ?? [];

  animations.forEach(killAnimation);
  registry.delete(sectionId);
  textReveals.forEach((restore, element) => {
    if (section === element || section?.contains?.(element)) {
      restore();
      textReveals.delete(element);
    }
  });
}

function killAll() {
  registry.forEach((animations) => animations.forEach(killAnimation));
  registry.clear();
}

function setReducedMotionState() {
  textReveals.forEach((restore) => restore());
  textReveals.clear();
  document.body.classList.remove('js-homepage-scroll');
  document.body.classList.remove('js-homepage-scroll-cinema');
  gsap.set(
    [
      '.hero-video__content',
      '.hero-video__player',
      '.whats-new__header',
      '.wn-card',
      '.disc-gallery__header',
      '.disc-card',
      '.sail-collection__section-header',
      '.sail-card',
      '.qna__header-left',
      '.qna__meta',
      '.qna__card',
      '.section-split__column--image img',
      '.section-split .placement-grid .prose > *',
      '.split-line__inner',
      '.media-blocks__item',
      '.shopify-section--collection-list .section-header',
      '.collection-list__item',
    ].join(','),
    { clearProps: 'all', opacity: 1, y: 0, x: 0, yPercent: 0, scale: 1, clipPath: 'none', filter: 'none' }
  );

  document.querySelectorAll('.qna').forEach((section) => section.classList.add('is-visible'));
}


function animateTextReveal(element, targetText, options = {}) {
  if (!element || !targetText) return null;

  const { duration = 1.4, ease = 'none', scrollTrigger, timeline, position = 0 } = options;
  const progress = { value: 0 };
  const hasInlineMarkup = element.children.length > 0;

  element.setAttribute('aria-label', targetText);

  if (!hasInlineMarkup) {
    textReveals.set(element, () => { element.textContent = targetText; });
    element.textContent = '';

    const tween = gsap.to(progress, {
      value: 1,
      duration,
      ease,
      scrollTrigger: timeline ? undefined : scrollTrigger,
      onUpdate() {
        const length = Math.round(progress.value * targetText.length);
        element.textContent = targetText.slice(0, length);
      },
    });

    if (timeline) {
      timeline.add(tween, position);
    }

    return tween;
  }

  const textNodes = [];
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      return node.nodeValue ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });

  while (walker.nextNode()) {
    textNodes.push({ node: walker.currentNode, text: walker.currentNode.nodeValue });
  }

  while (textNodes[0]?.text.trim() === '') {
    textNodes.shift().node.nodeValue = '';
  }

  while (textNodes[textNodes.length - 1]?.text.trim() === '') {
    textNodes.pop().node.nodeValue = '';
  }

  if (!textNodes.length) return null;

  textReveals.set(element, () => {
    textNodes.forEach((item) => { item.node.nodeValue = item.text; });
  });

  const fullTextLength = textNodes.reduce((total, item) => total + item.text.length, 0);

  textNodes.forEach((item) => {
    item.node.nodeValue = '';
  });

  const tween = gsap.to(progress, {
    value: 1,
    duration,
    ease,
    scrollTrigger: timeline ? undefined : scrollTrigger,
    onUpdate() {
      let remaining = Math.round(progress.value * fullTextLength);

      textNodes.forEach((item) => {
        const length = Math.min(item.text.length, Math.max(remaining, 0));

        item.node.nodeValue = item.text.slice(0, length);
        remaining -= length;
      });
    },
  });

  if (timeline) {
    timeline.add(tween, position);
  }

  return tween;
}

function splitTextLines(element) {
  if (!element || element.dataset.linesSplit === 'true') {
    return [...(element?.querySelectorAll('.split-line__inner') ?? [])];
  }

  if (element.children.length > 0) {
    return [];
  }

  const originalNodes = [...element.childNodes];
  const hadSplitClass = element.classList.contains('split-lines');
  const originalSplitState = element.dataset.linesSplit;
  textReveals.set(element, () => {
    element.replaceChildren(...originalNodes);
    if (!hadSplitClass) element.classList.remove('split-lines');
    if (originalSplitState === undefined) delete element.dataset.linesSplit;
    else element.dataset.linesSplit = originalSplitState;
  });

  const words = element.textContent.trim().split(/\s+/).filter(Boolean);

  element.textContent = '';
  element.classList.add('split-lines');

  words.forEach((word, index) => {
    if (index > 0) {
      element.appendChild(document.createTextNode(' '));
    }

    const span = document.createElement('span');
    span.className = 'split-word';
    span.textContent = word;
    element.appendChild(span);
  });

  const wordSpans = [...element.querySelectorAll('.split-word')];
  const lines = [];
  let currentLine = [];
  let lastTop = null;

  wordSpans.forEach((span) => {
    const top = span.offsetTop;

    if (lastTop !== null && top !== lastTop && currentLine.length) {
      lines.push(currentLine);
      currentLine = [];
    }

    currentLine.push(span);
    lastTop = top;
  });

  if (currentLine.length) {
    lines.push(currentLine);
  }

  element.textContent = '';
  const lineElements = [];

  lines.forEach((lineWords) => {
    const mask = document.createElement('span');
    mask.className = 'split-line';

    const inner = document.createElement('span');
    inner.className = 'split-line__inner';

    lineWords.forEach((word, index) => {
      if (index > 0) {
        inner.appendChild(document.createTextNode(' '));
      }

      inner.appendChild(word);
    });

    mask.appendChild(inner);
    element.appendChild(mask);
    lineElements.push(inner);
  });

  element.dataset.linesSplit = 'true';

  return lineElements;
}

function initHero(root = document) {
  const section = findSectionElement(root, '.shopify-section--hero-video', '.hero-video');
  if (!section) return;

  const text = section.querySelector('.hero-video__content');
  const players = section.querySelectorAll('.hero-video__player');
  if (!text || !players.length) return;

  const mm = gsap.matchMedia();

  // Desktop only: scrub-link the hero fade/zoom to scroll. On mobile the scrub
  // lag makes the top of the page feel unresponsive, so we leave the scroll
  // native and let the CSS entrance animations (and Ken Burns, off on mobile)
  // carry the hero.
  mm.add(CINEMA_QUERY, () => {
    // The Ken Burns CSS zoom would fight the scrub-driven scale, so disable it
    // while the timeline owns the players. Restored on revert.
    players.forEach((player) => {
      player.style.animation = 'none';
    });

    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: 'bottom top',
        scrub: 1.2,
      },
    });

    timeline
      .to(text, { y: -80, opacity: 0, ease: 'none' }, 0)
      .to(players, { scale: 1.12, opacity: 0.35, ease: 'none' }, 0);

    return () => {
      players.forEach((player) => {
        player.style.animation = '';
      });
    };
  });

  remember(section, mm);
}

function initWhatsNew(root = document) {
  const section = findSectionElement(root, '.shopify-section--whats-new-season', '.whats-new');
  if (!section) return;

  const header = section.querySelector('.whats-new__header');
  const cards = section.querySelectorAll('.wn-card');
  if (!cards.length) return;

  const timeline = gsap.timeline({
    scrollTrigger: { trigger: section, start: 'top 80%', once: true },
  });

  if (header) {
    timeline.from(header, { y: 40, opacity: 0, duration: 0.6, ease: 'power3.out' });
  }

  timeline.from(cards, { y: 60, opacity: 0, scale: 0.95, stagger: 0.1, duration: 0.5, ease: 'power2.out' }, header ? '-=0.3' : 0);
  remember(section, timeline);
}

function initDiscipline(root = document) {
  const section = findSectionElement(root, '.shopify-section--discipline-video-gallery', '.disc-gallery');
  if (!section) return;

  const shopifySection = section.closest('.shopify-section') ?? section;
  const nextSection = shopifySection.nextElementSibling;
  const header = section.querySelector('.disc-gallery__header');
  const track = section.querySelector('.disc-gallery__track');
  const spacer = section.querySelector('.disc-gallery__stack-spacer');
  const cards = gsap.utils.toArray(section.querySelectorAll('.disc-card'));
  if (!cards.length) return;

  if (header) {
    const headerTimeline = gsap.timeline({
      scrollTrigger: { trigger: section, start: 'top 85%', once: true },
    });

    headerTimeline.from(header, { y: 40, opacity: 0, duration: 0.6, ease: 'power3.out' });
    remember(section, headerTimeline);
  }

  const setStackSpacer = () => {
    if (!spacer || !track || !cards.length) return;

    const cardHeight = cards[cards.length - 1]?.offsetHeight || cards[0]?.offsetHeight || 0;
    const measuredGap =
      cards.length > 1 ? cards[1].offsetTop - cards[0].offsetTop - cards[0].offsetHeight : 0;
    const gap = measuredGap > 0 ? measuredGap : cardHeight * 0.35;
    const stackScroll = Math.max((cards.length - 1) * gap + cardHeight * 0.35, cardHeight);

    spacer.style.height = `${Math.round(stackScroll)}px`;
  };

  setStackSpacer();

  const pinEnd = nextSection
    ? {
        endTrigger: nextSection,
        end: 'top 20%',
      }
    : {
        end: 'bottom bottom',
      };

  cards.forEach((card, index) => {
    gsap.set(card, { zIndex: index + 1 });

    const pin = ScrollTrigger.create({
      trigger: card,
      start: 'top 20%',
      pin: true,
      pinSpacing: false,
      pinReparent: false,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onRefresh: setStackSpacer,
      ...pinEnd,
    });

    remember(section, pin);

    if (index < cards.length - 1) {
      // opacity instead of filter: brightness() — same darkening effect on a
      // dark background, without the cost of a compositing filter layer.
      const shrink = gsap.to(card, {
        scale: 0.92,
        opacity: 0.55,
        ease: 'none',
        scrollTrigger: {
          trigger: cards[index + 1],
          start: 'top bottom',
          end: 'top 20%',
          scrub: true,
          invalidateOnRefresh: true,
        },
      });

      remember(section, shrink);
    }
  });
}

function initSailRange(root = document) {
  const section = findSectionElement(root, '[data-section-type="sail-range-homepage-v2"]');
  if (!section) return;

  const header = section.querySelector('.sail-collection__section-header');
  const track = section.querySelector('.sail-collection__track');
  const cards = section.querySelectorAll('.sail-card');

  if (header) {
    const titleText = header.querySelector('.sail-collection__title-text');

    if (titleText) {
      const targetText = titleText.textContent.trim().toUpperCase();
      const textReveal = animateTextReveal(titleText, targetText, {
        duration: 1.4,
        ease: 'none',
        scrollTrigger: { trigger: section, start: 'top 85%', once: true },
      });

      remember(section, textReveal);
    } else {
      const headerTimeline = gsap.timeline({
        scrollTrigger: { trigger: section, start: 'top 85%', once: true },
      });

      headerTimeline.from(header, { y: 40, opacity: 0, duration: 0.6, ease: 'power3.out' });
      remember(section, headerTimeline);
    }
  }

  if (track && cards.length) {
    // Explicit end values: a `from` tween re-reads its destination from the
    // card's computed transform, and the .sail-card transform transition makes
    // a mid-animation ScrollTrigger.refresh() record a partial offset (cards
    // stay 40px out of line). fromTo pins the end state so it cannot drift.
    const cardsTween = gsap.fromTo(cards, { y: 40, opacity: 0 }, {
      scrollTrigger: { trigger: track, start: 'top 90%', once: true },
      y: 0,
      opacity: 1,
      stagger: { each: 0.06, grid: 'auto', from: 'start' },
      duration: 0.5,
      ease: 'power2.out',
    });

    remember(section, cardsTween);
  }

  if (header && track) {
    const mm = gsap.matchMedia();

    // Pin the header only in the cinema profile (matches the previous
    // desktop-only guard, now consistent with the rest of the homepage).
    mm.add(CINEMA_QUERY, () => {
      ScrollTrigger.create({
        trigger: section,
        start: 'top top+=40',
        end: () => `+=${track.offsetHeight * 0.6}`,
        pin: header,
        pinSpacing: false,
        anticipatePin: 1,
      });
    });

    remember(section, mm);
  }
}

function initQna(root = document) {
  const section = findSectionElement(root, '.shopify-section--qna-monty', '.qna');
  if (!section) return;
  if (section.dataset.scrollAnimations === 'false') return;

  const header = section.querySelector('.qna__header-left');
  const meta = section.querySelector('.qna__meta');
  const cards = section.querySelectorAll('.qna__card');
  if (!header && !meta && !cards.length) return;

  section.classList.remove('is-visible');
  const timeline = gsap.timeline({
    scrollTrigger: { trigger: section, start: 'top 80%', once: true },
  });

  if (header) {
    timeline.fromTo(header, { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' }, 0);
  }

  if (meta) {
    timeline.fromTo(meta, { opacity: 0 }, { opacity: 1, duration: 0.5, ease: 'power2.out' }, 0.1);
  }

  if (cards.length) {
    timeline.fromTo(cards, { opacity: 0, scale: 0.92 }, { opacity: 1, scale: 1, stagger: 0.1, duration: 0.5, ease: 'power3.out' }, 0.15);
  }

  remember(section, timeline);
}

function initMontyStory(root = document) {
  const section = findSectionElement(root, '.shopify-section--text-with-media', '.section-split');
  if (!section) return;

  const image = section.querySelector('.section-split__column--image img, .content-box__background-media img');
  const prose = section.querySelector('.placement-grid .prose');

  if (image) {
    const mm = gsap.matchMedia();

    // Parallax is scroll-scrubbed (continuous repaint) — desktop only. The text
    // reveal below runs on all viewports and carries the section on mobile.
    mm.add(CINEMA_QUERY, () => {
      gsap.to(image, {
        scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: true },
        yPercent: -12,
        ease: 'none',
      });
    });

    remember(section, mm);
  }

  if (!prose) return;

  const heading = prose.querySelector('h1, h2, h3, h4, h5, h6');
  const paragraph = prose.querySelector('.max-w-sm p') ?? prose.querySelector('p');
  const paragraphWrap = paragraph?.closest('.max-w-sm') ?? paragraph;
  const extras = [...prose.children].filter((block) => {
    if (heading && (block === heading || block.contains(heading))) return false;
    if (paragraphWrap && block === paragraphWrap) return false;

    return true;
  });

  const timeline = gsap.timeline({
    scrollTrigger: { trigger: section, start: 'top 75%', once: true },
  });

  if (extras.length) {
    timeline.from(extras, { y: 24, opacity: 0, duration: 0.5, stagger: 0.08, ease: 'power3.out' }, 0);
  }

  if (heading) {
    animateTextReveal(heading, heading.textContent.trim(), {
      duration: 1.4,
      ease: 'none',
      timeline,
      position: 0.1,
    });
  }

  if (paragraph) {
    const lines = splitTextLines(paragraph);

    if (lines.length) {
      gsap.set(lines, { yPercent: 120 });
      timeline.to(
        lines,
        {
          yPercent: 0,
          duration: 1,
          ease: 'power4.out',
          stagger: 0.09,
        },
        heading ? 0.55 : 0.15
      );
    } else if (paragraphWrap) {
      timeline.from(
        paragraphWrap,
        {
          y: 24,
          opacity: 0,
          duration: 0.6,
          ease: 'power3.out',
        },
        heading ? 0.55 : 0.15
      );
    }
  }

  remember(section, timeline);
}

function initMediaBlocks(root = document) {
  const section = findSectionElement(root, '.shopify-section--media-blocks', '.media-blocks');
  if (!section) return;

  const items = section.querySelectorAll('.media-blocks__item');
  if (!items.length) return;

  const tween = gsap.from(items, {
    scrollTrigger: { trigger: section, start: 'top 80%', once: true },
    clipPath: (index) => (index === 0 ? 'inset(0 100% 0 0)' : 'inset(0 0 0 100%)'),
    opacity: 0,
    stagger: 0.2,
    duration: 0.9,
    ease: 'power4.out',
  });

  remember(section, tween);
}

function initCategories(root = document) {
  const wrapper = findSectionElement(root, '.shopify-section--collection-list');
  if (!wrapper) return;

  const section = wrapper.querySelector('.collection-list');
  const header = wrapper.querySelector('.section-header, [class*="section-header"]');
  const cards = wrapper.querySelectorAll('.collection-list__item');
  if (!cards.length) return;

  const timeline = gsap.timeline({
    scrollTrigger: { trigger: section ?? wrapper, start: 'top 85%', once: true },
  });

  if (header) {
    timeline.from(header, { y: 40, opacity: 0, duration: 0.6, ease: 'power3.out' });
  }

  timeline.from(cards, { y: 50, opacity: 0, stagger: 0.1, duration: 0.5, ease: 'power2.out' }, header ? '-=0.25' : 0);
  remember(wrapper, timeline);
}

function initSection(root = document) {
  killSection(root);

  initHero(root);
  initWhatsNew(root);
  initDiscipline(root);
  initSailRange(root);
  initQna(root);
  initMontyStory(root);
  initMediaBlocks(root);
  initCategories(root);
}

function initHomepageScroll(root = document) {
  killSection(root);

  if (!isEnabled()) {
    killAll();
    teardownCinemaBodyClass();
    setReducedMotionState();
    return;
  }

  document.body.classList.add('js-homepage-scroll');
  setupCinemaBodyClass();
  initSection(root);
  requestAnimationFrame(() => ScrollTrigger.refresh());
}

function debounce(fn, wait) {
  let timeout;

  return (...args) => {
    window.clearTimeout(timeout);
    timeout = window.setTimeout(() => fn(...args), wait);
  };
}

function refreshAfterMedia() {
  const media = document.querySelectorAll('.shopify-section--hero-video img, .shopify-section--hero-video video, main img, main video');

  media.forEach((item) => {
    const eventName = item.tagName === 'VIDEO' ? 'loadeddata' : 'load';

    if (item.tagName === 'IMG' && item.complete) return;
    if (item.tagName === 'VIDEO' && item.readyState >= 2) return;

    item.addEventListener(eventName, () => ScrollTrigger.refresh(), { once: true });
  });
}

function startHomepageScroll() {
  initHomepageScroll();
  refreshAfterMedia();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startHomepageScroll, { once: true });
} else {
  startHomepageScroll();
}

window.addEventListener('load', () => ScrollTrigger.refresh());
window.addEventListener('resize', debounce(() => ScrollTrigger.refresh(), 150));

// Keep content static when the viewport or input no longer supports motion.
cinemaQuery.addEventListener('change', () => {
  killAll();
  initHomepageScroll();
});

motionReduceQuery.addEventListener?.('change', () => {
  killAll();
  initHomepageScroll();
});

document.addEventListener('shopify:section:load', (event) => {
  if (!isEnabled()) {
    setReducedMotionState();
    return;
  }

  initSection(event.target);
  refreshAfterMedia(event.target);
  requestAnimationFrame(() => ScrollTrigger.refresh());
});

document.addEventListener('shopify:section:unload', (event) => {
  killSection(event.target);
  requestAnimationFrame(() => ScrollTrigger.refresh());
});

export { remember };
